/**
 * Was die Nachrichtenliste ueber ihren eigenen Ladezustand SAGT -- an EINER
 * Stelle.
 *
 * Warum es das gibt: `loading` wurde im Template an genau EINER Stelle
 * ausgewertet, und zwar negativ -- `v-if="!loading && messages.length === 0"`
 * unterdrueckte den Leertext. Eine positive Anzeige gab es nicht. Beim
 * Erstaufruf wartet mounted() auf 2,62 MB (gemessen, 227 Nachrichten); sichtbar
 * waren in dieser Zeit Kopfzeile, Filter, Fortschrittsbalken 0/0 und darunter
 * leere Flaeche. Die Begruendung dagegen steht seit dem 13.08. an der
 * Reflexionsseite und gilt hier woertlich: ohne Ladeanzeige ist eine ladende
 * Seite von einer haengenden nicht zu unterscheiden.
 *
 * ACHTUNG, DREI Zustaende und nicht einer -- sie sagen Verschiedenes:
 *
 *   ERSTLADUNG  es ist noch nichts da. Die Flaeche ist leer, WEIL geladen wird.
 *   NACHLADEN   es steht etwas da, und es wird gerade ersetzt. Die Liste bleibt
 *               stehen; nur die Aktualitaetszeile sagt, dass sie arbeitet.
 *   FEHLER      es ist nichts angekommen, und der Grund ist bekannt.
 *
 * ACHTUNG, der Fehlerzustand ist nicht Kuer, sondern die Voraussetzung dafuer,
 * dass die Ladeanzeige ueberhaupt gebaut werden darf. fetchMessages() fing
 * jeden Fehler ab und schrieb ihn nach console.error; sichtbar blieb nichts.
 * Eine Ladeanzeige OHNE Fehlerzweig haette dort ewig gedreht -- schlimmer als
 * gar keine.
 *
 * ROT, und der eigentliche Anlass fuer LEER_UNBEKANNT: nach einem
 * fehlgeschlagenen Ladevorgang ist `!loading && messages.length === 0` WAHR.
 * Die Seite behauptete dann "Keine Nachrichten gefunden." -- eine Aussage ueber
 * den Bestand, die sie gar nicht getroffen hat. Der Leertext gilt nur nach
 * einer ERFOLGREICHEN Ladung.
 *
 * Dieselbe Konvention wie hinweise.js (NEU_BEWERTEN), shortcuts.js (KUERZEL),
 * merkliste.js (MERK_MAX): eine Aussage bekommt einen NAMEN, der Wortlaut
 * daneben ist frei. Der Test prueft Schluessel und Bindung -- nie die Prosa.
 */

/**
 * Die fuenf Aussagen, die die Liste ueber ihren Ladezustand treffen MUSS.
 *
 * erstladung  -- es laeuft etwas, und deshalb ist hier noch nichts
 * nachladen   -- der gezeigte Stand wird gerade abgeloest
 * fehler      -- es ist nichts angekommen (der Grund kommt daneben, s. u.)
 * leer        -- geladen, und es gibt im ganzen Bestand nichts
 * sessionLeer -- geladen, aber zu DIESER Session gehoert nichts
 *
 * ROT, und der Anlass fuer sessionLeer (26.08.): seit GET /messages die
 * gewaehlte Session serverseitig auswertet, ist eine leere Liste eine
 * RICHTIGE Antwort -- naemlich dann, wenn der Session keine Kategorie
 * zugeordnet ist oder ihre Kategorien im Bestand nicht vorkommen. Der Satz
 * "Keine Nachrichten gefunden." behauptet an dieser Stelle etwas ueber den
 * BESTAND, das die Seite gar nicht gemessen hat: der Bestand ist voll, nur
 * diese Session ist leer. Das ist byteweise dieselbe Fehlerklasse, wegen der
 * LEER_UNBEKANNT und der Fehlerzweig oben ueberhaupt gebaut wurden.
 *
 * ACHTUNG, der Name faengt bewusst NICHT mit "leer" an. "leerSession"
 * waere der naheliegende Name gewesen -- und er haette LADEN.leer als
 * PRAEFIX enthalten. Bestandstests suchen ihre Stelle mit
 * text.index("LADEN.leer"); der Treffer waere ab sofort dieser Schluessel
 * gewesen, und die Pruefung haette lautlos den falschen Block gemessen.
 * Gemessen: test_der_leertext_ist_an_die_zweige_davor_gekettet fiel genau
 * daran.
 *
 * ACHTUNG, was sessionLeer NICHT sagt: WARUM die Session leer ist. Die Liste
 * kann "keine Kategorie zugeordnet" nicht von "Kategorien ohne Nachrichten"
 * unterscheiden -- der Server antwortet in beiden Faellen mit einer leeren
 * Liste. Wer den Unterschied braucht, findet ihn in
 * GET /session-categories/im-bestand, der genau dafuer existiert (er weist
 * `verwaist` und `randweiss` aus). Ein geratener Grund waere hier schlechter
 * als keiner.
 */
export const LADEN = {
  erstladung: "Nachrichten werden geladen …",
  nachladen: "wird aktualisiert …",
  fehler: "Die Nachrichten konnten nicht geladen werden.",
  leer: "Keine Nachrichten gefunden.",
  sessionLeer: "Zu dieser Session gehören keine Nachrichten.",
};

/**
 * Wie lang ein Ladehinweis hoechstens sein darf.
 *
 * Dieselbe Begruendung wie HINWEIS_MAX_ZEICHEN in hinweise.js: ohne Schranke
 * waechst ein solcher Satz zum Absatz zurueck, weil jeder Zusatz fuer sich
 * begruendet aussieht. Ein Ladehinweis sagt, DASS etwas laeuft -- nicht, wie es
 * funktioniert.
 */
export const LADEHINWEIS_MAX_ZEICHEN = 80;

/**
 * Der Grund eines Ladefehlers, so knapp wie er belastbar ist.
 *
 * ACHTUNG: hier wird NICHTS geraten. Liefert der Fehler keinen Statuscode und
 * keine Meldung, bleibt es beim blossen "fehler" oben -- ein erfundener Grund
 * ("Server nicht erreichbar") waere schlechter als keiner, weil er die naechste
 * Suche in die falsche Richtung schickt.
 *
 * Der Statuscode steht vorn, weil er die einzige Angabe ist, die immer dieselbe
 * Bedeutung hat. 401 bekommt einen eigenen Satz: die Liste ruft listMessages()
 * ueber nacktes axios OHNE den 401-Interceptor (api/messages.js:19-20), es gibt
 * dort also keine Weiterleitung zur Anmeldung, die den Fall sonst erklaeren
 * wuerde.
 */
export function ladeFehlerGrund(fehler) {
  const status = fehler?.response?.status;
  if (status === 401 || status === 403) {
    return "Die Anmeldung gilt nicht mehr.";
  }
  if (typeof status === "number") {
    const detail = fehler?.response?.data?.detail;
    return typeof detail === "string" && detail.trim()
      ? `HTTP ${status}: ${detail.trim()}`
      : `HTTP ${status}`;
  }
  const meldung = fehler?.message;
  return typeof meldung === "string" && meldung.trim() ? meldung.trim() : "";
}

/**
 * Was die Nachrichtenliste ueber den Umfang der gewaehlten Session ZUSAETZLICH
 * sagt (seit 01.10.; vorher der Treffer-Leertext des Anheftens). Die Zahlen
 * kommen aus GET /session-categories/andere-server, nicht aus der Liste.
 *
 * andereServer -- unter den Kategorien der Session liegen Nachrichten auf
 *     ANDEREN Servern als dem aktiven. Steht im Tooltip an der Umfangszeile,
 *     je Server Name und Zahl; teile = [{ name, nachrichten }].
 * liveSession -- die gewaehlte Session ist KEINE asynchrone: die Auswahl
 *     zeigt alle Sessions des Credentials, eine Live-Session hat keine
 *     Zuordnung und damit nie Nachrichten.
 * beendenOhneNachrichten -- steht im Dialog VOR dem Beenden: ohne
 *     Nachrichten im Umfang schreibt der Comment-Worker nichts.
 *
 * ACHTUNG, die Namen fangen NICHT mit "leer" oder "sessionLeer" an --
 * Bestandstests suchen ihre Stelle mit text.index("LADEN.sessionLeer"),
 * und ein Schluessel mit diesem Praefix lenkte sie lautlos um (26.08.).
 */
export const TREFFER = {
  andereServer: (teile) =>
    `Nachrichten auf anderen Servern: ${teile.map((t) => `„${t.name}“ ${t.nachrichten}`).join(" · ")}.`,
  liveSession: "Diese Session ist eine Live-Session — Nachrichten gehören nur zu asynchronen Sessions.",
  beendenOhneNachrichten: "Diese Session hat 0 Nachrichten im Umfang — Beenden erzeugt keine Kommentare.",
};
